import { NavLink } from 'react-router-dom';
import {
  FolderKanban,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Building2,
  Brain,
  Server,
  Wand2,
  TrendingUp,
  ListOrdered,
  Home,
  Shield,
  Network,
  Bot,
} from 'lucide-react';
import { useAppStore } from '../../stores/appStore';
import Tooltip from '../ui/Tooltip';

/**
 * Agent Builder cockpit nav entry (Phase 2) — ADDITIVE & FLAG-GATED. Appended to
 * the sidebar ONLY when VITE_ENABLE_AGENT_BUILDER is truthy (mirrors the backend
 * `enable_agent_builder` flag + the App.tsx route). With the flag OFF the nav is
 * byte-identical to before.
 */
const AGENT_BUILDER_ENABLED =
  String(import.meta.env.VITE_ENABLE_AGENT_BUILDER ?? '').toLowerCase() === 'true';

const baseNavigation = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: Home,
  },
  {
    name: 'Case Library',
    href: '/case-library',
    icon: FolderKanban,
  },
  {
    name: 'AI Assistant',
    href: '/chat',
    icon: Sparkles,
  },
  {
    name: 'Graph Assistant',
    href: '/graph-assistant',
    icon: Network,
  },
  {
    name: 'IntelliKnow™ Discovery',
    href: '/cases/new',
    icon: Wand2,
  },
  {
    name: 'Domain Research',
    href: '/domain-packs',
    icon: Brain,
  },
  {
    name: 'Enterprise Systems',
    href: '/enterprise-systems',
    icon: Server,
  },
  {
    name: 'Company Research',
    href: '/companies',
    icon: Building2,
  },
  {
    name: 'Industry Research',
    href: '/industries',
    icon: TrendingUp,
  },
  {
    name: 'Research Queue',
    href: '/research-queue',
    icon: ListOrdered,
  },
];

// Additive, flag-gated cockpit entry. When the flag is OFF this list is empty and
// `navigation` is identical to `baseNavigation`.
const agentBuilderNavigation = AGENT_BUILDER_ENABLED
  ? [
      {
        name: 'Agent Builder',
        href: '/agent-builder',
        icon: Bot,
      },
    ]
  : [];

const navigation = [...baseNavigation, ...agentBuilderNavigation];

const adminNavigation = [
  {
    name: 'Admin Dashboard',
    href: '/admin',
    icon: Shield,
  },
];

export default function Sidebar() {
  const sidebarCollapsed = useAppStore((state) => state.sidebarCollapsed);
  const toggleSidebar = useAppStore((state) => state.toggleSidebar);
  const currentUser = useAppStore((state) => state.currentUser);

  const isAdmin = currentUser?.role === 'Admin';

  // A nav item collapses to an icon-only target; when the rail is collapsed we
  // surface the label as a right-side tooltip so the icons stay identifiable.
  type NavItem = (typeof navigation)[number];
  const renderNavItem = (item: NavItem, activeClass: string) => {
    const link = (
      <NavLink
        key={item.name}
        to={item.href}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-3 mx-2 rounded-lg transition-colors ${
            isActive ? activeClass : 'text-neutral-700 hover:bg-neutral-100'
          }`
        }
      >
        <item.icon className="w-5 h-5 flex-shrink-0" />
        {!sidebarCollapsed && <span className="font-medium text-sm">{item.name}</span>}
      </NavLink>
    );

    return sidebarCollapsed ? (
      <Tooltip key={item.name} content={item.name} position="right" variant="slate">
        {link}
      </Tooltip>
    ) : (
      link
    );
  };

  return (
    <aside
      className={`fixed left-0 top-16 bottom-0 z-30 bg-white border-r border-neutral-200 transition-all duration-300 ${
        sidebarCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      <nav className="h-full flex flex-col">
        {/* Navigation Links */}
        <div className="flex-1 py-4 space-y-1">
          {navigation.map((item) => renderNavItem(item, 'bg-primary-50 text-primary-700'))}

          {/* Admin Section */}
          {isAdmin && (
            <>
              <div className="mx-4 my-2 border-t border-neutral-200" />
              {adminNavigation.map((item) =>
                renderNavItem(item, 'bg-secondary-50 text-secondary-700'),
              )}
            </>
          )}
        </div>

        {/* Collapse Toggle */}
        <div className="p-4 border-t border-neutral-200">
          <button
            onClick={toggleSidebar}
            className="w-full flex items-center justify-center gap-2 p-2 rounded-lg hover:bg-neutral-100 transition-colors text-neutral-600"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Collapse</span>
              </>
            )}
          </button>
        </div>
      </nav>
    </aside>
  );
}
